/**
 * Integration requests service — MOCK-FIRST (CT-3895 / CT-3898).
 *
 * Backs the Request Integration form's submit (CT-4176) and the Requests
 * Management inbox (CT-3898). A module-level mutable seed is the shared source:
 * submitted requests append here and show up in the inbox within the same
 * process. Platform envelope, snake_case, `TODO(CT-3904 / backend)` per function.
 */

import { envelope } from '../shared/utils/mockServiceHelpers.js';

/**
 * Demo end-user the "user" persona impersonates (CT-4203). Their seed requests
 * populate the end-user "My requests" view so the demo is realistic. MOCK-only:
 * with real auth + RBAC (CT-3904 / CT-3900) the owner id comes from the session.
 */
export const DEMO_END_USER = { id: 'u-danilo', name: 'Danilo Petrov' };

const buildSeedRequests = () => [
  {
    ticket_number: 'REQ-1042',
    name: 'HubSpot',
    vendor: 'HubSpot, Inc.',
    requester: 'Danilo Petrov',
    requester_id: 'u-danilo',
    requester_role: 'Sales Operations',
    workspace: 'Contoso',
    department: 'sales',
    scope: 'org',
    connector_type: 'official',
    action_types: ['read', 'write'],
    status: 'pending',
    priority: 'high',
    expected_users: 24,
    timeline_weeks: 2,
    data_sensitivity: { pii: 'yes', financial: 'no' },
    business_intent:
      'We just moved the SDR team from Outreach to HubSpot. Need to pull engagement data into our pipeline forecast workflow so the Ramp Reps agent can prioritize follow-ups by intent score.',
    date_created: '2026-08-19T12:00:00.000Z',
  },
  {
    ticket_number: 'REQ-1044',
    name: 'Datadog',
    vendor: 'Datadog, Inc.',
    requester: 'Elena Soto',
    requester_id: 'u-elena',
    requester_role: 'Platform Engineering',
    workspace: 'Contoso',
    department: 'engineering',
    scope: 'org',
    connector_type: 'official',
    action_types: ['read'],
    status: 'pending',
    priority: 'high',
    expected_users: 15,
    timeline_weeks: 3,
    data_sensitivity: { pii: 'no', financial: 'no' },
    business_intent:
      'On-call alerts need to route through the Incident Commander agent to spin up an investigation channel automatically.',
    date_created: '2026-08-19T11:30:00.000Z',
  },
  {
    ticket_number: 'REQ-1039',
    name: 'PagerDuty',
    vendor: 'PagerDuty',
    requester: 'Maria Gomez',
    requester_id: 'u-maria',
    requester_role: 'SRE Lead',
    workspace: 'Contoso',
    department: 'engineering',
    scope: 'tenant',
    connector_type: 'official',
    action_types: ['read', 'write'],
    status: 'quoted',
    priority: 'medium',
    expected_users: 12,
    timeline_weeks: 4,
    quote_usd: 18500,
    eta_weeks: 4,
    data_sensitivity: { pii: 'no', financial: 'no' },
    business_intent: 'Route production alerts to on-call so the Standby agent knows who to ping.',
    date_created: '2026-08-18T13:00:00.000Z',
  },
  {
    ticket_number: 'REQ-1031',
    name: 'DocuSign',
    vendor: 'DocuSign',
    requester: 'Maria Gomez',
    requester_id: 'u-maria',
    requester_role: 'Operations',
    workspace: 'Contoso',
    department: 'operations',
    scope: 'org',
    connector_type: 'official',
    action_types: ['read', 'write'],
    status: 'in_progress',
    priority: 'medium',
    expected_users: 30,
    timeline_weeks: 3,
    quote_usd: 9200,
    eta_weeks: 3,
    data_sensitivity: { pii: 'yes', financial: 'yes' },
    business_intent: 'E-signature for contracts so the Finance agent can track signed agreements.',
    date_created: '2026-08-12T16:40:00.000Z',
  },
  {
    ticket_number: 'REQ-1024',
    name: 'Miro',
    vendor: 'Miro',
    requester: 'Danilo Petrov',
    requester_id: 'u-danilo',
    requester_role: 'Product',
    workspace: 'Contoso',
    department: 'product',
    scope: 'tenant',
    connector_type: 'official',
    action_types: ['read', 'write'],
    status: 'delivered',
    priority: 'low',
    expected_users: 8,
    timeline_weeks: 1,
    data_sensitivity: { pii: 'no', financial: 'no' },
    business_intent: 'Whiteboards for planning so the Roadmap agent can summarize sessions.',
    date_created: '2026-07-11T11:15:00.000Z',
  },
  {
    ticket_number: 'REQ-1018',
    name: 'Legacy FTP',
    vendor: 'Internal',
    requester: 'Maria Gomez',
    requester_id: 'u-maria',
    requester_role: 'Data Engineering',
    workspace: 'Contoso',
    department: 'engineering',
    scope: 'tenant',
    connector_type: 'custom',
    action_types: ['read'],
    status: 'rejected',
    priority: 'low',
    expected_users: 5,
    timeline_weeks: 2,
    data_sensitivity: { pii: 'no', financial: 'no' },
    business_intent: 'Pull nightly exports for the Demand Gen dashboard.',
    date_created: '2026-07-02T08:00:00.000Z',
    decline_reason: 'Superseded by the S3 connector.',
  },
];

const TICKET_COUNTER_SEED = 1045;
let mockRequests = buildSeedRequests();
let ticketCounter = TICKET_COUNTER_SEED;

/** Test-only: reset the mutable seed + ticket counter. */
export const __resetMockRequestsForTests = () => {
  mockRequests = buildSeedRequests();
  ticketCounter = TICKET_COUNTER_SEED;
};

/**
 * Lightweight change subscription. Any mutation (submit / patch / bulk) notifies
 * so an already-mounted inbox (useRequestsInbox) re-fetches without the user
 * having to navigate away and back — e.g. a request filed from the modal shows
 * up in the list immediately. Mirrors the personaStore pattern.
 * TODO(CT-3904 / backend): real-time updates will come from the platform.
 */
const requestsListeners = new Set();
const notifyRequestsChanged = () => requestsListeners.forEach((fn) => fn());
export const subscribeRequests = (fn) => {
  requestsListeners.add(fn);
  return () => requestsListeners.delete(fn);
};

/**
 * Submit a new integration request (CT-4176).
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/integrations/requests`.
 * @param {object} body - snake_case request payload
 * @param {string} [nowIso] - server assigns the timestamp; injected here since
 *   `Date` is unavailable in some environments. Defaults to null.
 */
export const submitIntegrationRequest = (body, nowIso = null) => {
  const ticket_number = `REQ-${ticketCounter++}`;
  const record = {
    ...body,
    ticket_number,
    status: 'pending',
    date_created: nowIso, // TODO(CT-3904 / backend): server assigns the timestamp
  };
  mockRequests = [record, ...mockRequests];
  notifyRequestsChanged();
  return Promise.resolve(envelope({ ticket_number, status: 'pending', ...body }));
};

/**
 * List requests (paginated/filtered) for the inbox (CT-3898).
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/integrations/requests/list`.
 */
export const listRequests = (params = {}) => {
  const { status, requester, requester_id, search } = params;
  const q = (search ?? '').trim().toLowerCase();
  const items = mockRequests
    .filter((r) => {
      if (status && status !== 'all' && r.status !== status) return false;
      if (requester && r.requester !== requester) return false;
      if (requester_id && r.requester_id !== requester_id) return false;
      if (q && !`${r.name} ${r.requester} ${r.vendor} ${r.scope ?? ''} ${r.ticket_number}`.toLowerCase().includes(q))
        return false;
      return true;
    })
    .sort((a, b) => new Date(b.date_created || 0) - new Date(a.date_created || 0));
  return Promise.resolve(envelope(items, { total: items.length }));
};

/**
 * Recipient-facing message the platform panel will render, per event (CT-4204
 * Event→message map). The payload carries the i18n key + params instead of a
 * baked string so the platform localizes at render time (en/es).
 * Keys live under `integrations.requests.notifications.*`.
 */
const NOTIFY_MESSAGE_KEY = {
  approved: 'integrations.requests.notifications.approved',
  rejected: 'integrations.requests.notifications.rejected',
  in_progress: 'integrations.requests.notifications.inProgress',
  comment: 'integrations.requests.notifications.comment',
  submitted: 'integrations.requests.notifications.submitted',
};

/**
 * Fire a notification for a request event (CT-4204) — approved / rejected /
 * in_progress / comment (to the requester); submitted (to admins). Each payload
 * deep-links to the request thread and carries the recipient message (i18n key
 * + params) ready for the platform panel to localize.
 *
 * Delivery is PLATFORM-level (the AIMS-OS notification bell/panel, likely via
 * @aimsos/websocket) and owned outside this domain. In V1 this is a stub that
 * returns the payload; the actor gets a local toast at the call site.
 * TODO(CT-3904 / backend + platform notifications): route to the real channel.
 */
export const notifyRequestEvent = (event, ticketNumber, meta = {}) => {
  const req = mockRequests.find((r) => r.ticket_number === ticketNumber);
  const recipient = event === 'submitted' ? 'admins' : req?.requester_id ?? req?.requester ?? null;
  const message_params = {
    name: req?.name ?? meta.name ?? '',
    requester: req?.requester ?? meta.requester ?? '',
    reason: meta.reason ?? '',
    admin: meta.admin ?? '',
    preview: meta.preview ?? '',
  };
  return Promise.resolve(
    envelope({
      event, // 'approved' | 'rejected' | 'in_progress' | 'comment' | 'submitted'
      ticket_number: ticketNumber,
      recipient,
      deep_link: `/admin-studio/requests?ticket=${ticketNumber}`,
      message_key: NOTIFY_MESSAGE_KEY[event] ?? null,
      message_params,
      ...meta,
    }),
  );
};

/** Single request by ticket number (CT-4201). */
export const getRequest = (ticketNumber) =>
  Promise.resolve(envelope(mockRequests.find((r) => r.ticket_number === ticketNumber) ?? null));

const patchRequest = (ticketNumber, patch) => {
  mockRequests = mockRequests.map((r) =>
    r.ticket_number === ticketNumber ? { ...r, ...patch } : r,
  );
  notifyRequestsChanged();
  return Promise.resolve(envelope(mockRequests.find((r) => r.ticket_number === ticketNumber)));
};

/** Approve a request (CT-4201). TODO(CT-3904 / backend). */
export const approveRequest = (ticketNumber, note) =>
  patchRequest(ticketNumber, { status: 'approved', approve_note: note ?? null });

/** Reject / decline a request with a required reason (CT-4201). TODO(CT-3904 / backend). */
export const rejectRequest = (ticketNumber, reason) =>
  patchRequest(ticketNumber, { status: 'rejected', decline_reason: reason });

/**
 * Approve → kicks off the build (CT-4201). Per the prototype, approving a request
 * moves it straight to `in_progress` ("Build in progress"), not a separate
 * `approved` state. TODO(CT-3904 / backend).
 */
export const markRequestInProgress = (ticketNumber) =>
  patchRequest(ticketNumber, { status: 'in_progress' });

/** Mark a built request as delivered — live in the catalog (CT-4201). */
export const markRequestDelivered = (ticketNumber) =>
  patchRequest(ticketNumber, { status: 'delivered' });

/** Send a quote (amount + ETA weeks + what's included) — moves to `quoted` (CT-4201). */
export const sendRequestQuote = (ticketNumber, { amount, weeks, note } = {}) =>
  patchRequest(ticketNumber, {
    status: 'quoted',
    quote_usd: amount ?? null,
    eta_weeks: weeks ?? null,
    quote_note: note ?? null,
  });

/** Append a comment to a request thread (CT-4201). TODO(CT-3904 / backend). */
export const addRequestComment = (ticketNumber, body, author = 'You') => {
  const req = mockRequests.find((r) => r.ticket_number === ticketNumber);
  if (req) {
    const comments = [...(req.comments ?? []), { id: `c-${(req.comments?.length ?? 0) + 1}`, author, body, at: null }];
    return patchRequest(ticketNumber, { comments });
  }
  return Promise.resolve(envelope(null));
};

/** Bulk status update over a selection, recorded as a single audit entry (CT-4200). */
export const bulkUpdateRequests = (ticketNumbers, status) => {
  mockRequests = mockRequests.map((r) =>
    ticketNumbers.includes(r.ticket_number) ? { ...r, status } : r,
  );
  notifyRequestsChanged();
  return Promise.resolve(envelope({ updated: ticketNumbers.length, status }));
};

/** Counts per status bucket for the inbox header/tabs (CT-3898). */
export const getRequestCounts = () => {
  const counts = mockRequests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});
  return Promise.resolve(
    envelope({ total: mockRequests.length, waiting_on_you: counts.pending ?? 0, by_status: counts }),
  );
};
